/**
 * @file Album Folder Album Folder Photos Reducer.
 */
import uniqBy from 'lodash.uniqby';

import { ALBUM_FOLDER_UPLOAD_SUCCESS } from '../actions/album-folder-upload';
import {
  ALBUM_FOLDER_PHOTOS_DELETE_REQUEST,
  ALBUM_FOLDER_PHOTOS_DELETE_SUCCESS,
  ALBUM_FOLDER_PHOTOS_DELETE_FAILURE,
  ALBUM_FOLDER_PHOTOS_DO_NOT_SHOW_DELETE_MODAL,
  ALBUM_FOLDER_PHOTOS_TOGGLE_MULTI_SELECT,
  ALBUM_FOLDER_PHOTOS_TOGGLE_PHOTO_ON_SELECT_LIST,
  ALBUM_FOLDER_PHOTOS_TOGGLE_TARGET_ALBUM,
  ALBUM_FOLDER_PHOTOS_TOGGLE_ASSIGNED_ALBUM_FOLDER_TAG_ID,
  ALBUM_FOLDER_PHOTOS_ASSIGN_REQUEST,
  ALBUM_FOLDER_PHOTOS_ASSIGN_SUCCESS,
  ALBUM_FOLDER_PHOTOS_ASSIGN_FAILURE,
  ALBUM_FOLDER_PHOTOS_DO_NOT_SHOW_ASSIGN_CONFIRMATION,
  ALBUM_FOLDER_PHOTOS_SELECT_ALL_FOR_REVIEW,
  ALBUM_FOLDER_PHOTOS_CLEAR_TARGET_ALBUMS,
  ALBUM_FOLDER_PHOTOS_CLEAR_ASSIGNED_ALBUM_FOLDER_TAG_IDS,
  ALBUM_FOLDER_PHOTOS_SELECT_ALL,
  ALBUM_FOLDER_PHOTOS_DESELECT_ALL,
  ALBUM_FOLDER_PHOTOS_SELECT_PHOTOS,
  ALBUM_FOLDER_PHOTOS_DESELECT_PHOTOS,
  ALBUM_FOLDER_PHOTOS_RESET,
} from '../actions/album-folder-photos';
import { UPLOAD_RESET } from '../apps/pro/redux/fileUpload/fileUploadActions';

export const initialState = {
  albumFolderId: '',
  photos: [],
  selectedPhotos: [],
  deleting: false,
  deleted: [],
  multiSelect: false,
  error: null,
  targetAlbums: [],
  assigning: false,
  selectAll: false,
  assignedAlbumFolderTagIds: [],
};

const albumFolderUploadsReducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case ALBUM_FOLDER_UPLOAD_SUCCESS:
      return {
        ...state,
        photos: action.isRetry
          ? [...state.photos, ...action.uploadedPhotoItemIds]
          : [...action.uploadedPhotoItemIds],
      };

    case ALBUM_FOLDER_PHOTOS_DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      };

    case ALBUM_FOLDER_PHOTOS_DELETE_SUCCESS: {
      const photoIds = action.photoIds || []; // may come empty from selectAll

      return {
        ...state,
        photos: state.photos.filter(photoId => !photoIds.includes(photoId)),
        selectedPhotos: [],
        deleting: false,
        multiSelect: false,
        selectAll: false,
        deleted: [...state.deleted, ...photoIds],
      };
    }

    case ALBUM_FOLDER_PHOTOS_DELETE_FAILURE:
      return {
        ...state,
        error: action.error,
        deleting: false,
      };

    case ALBUM_FOLDER_PHOTOS_DO_NOT_SHOW_DELETE_MODAL:
      return {
        ...state,
        showDeletePhotoModal: false,
      };
    case ALBUM_FOLDER_PHOTOS_TOGGLE_PHOTO_ON_SELECT_LIST:
      return {
        ...state,
        selectedPhotos: state.selectedPhotos.includes(action.photoId)
          ? state.selectedPhotos.filter(photoId => photoId !== action.photoId)
          : [...state.selectedPhotos, action.photoId],
      };

    case ALBUM_FOLDER_PHOTOS_TOGGLE_MULTI_SELECT:
      return {
        ...state,
        multiSelect: !state.multiSelect,
        selectedPhotos: state.multiSelect ? [] : state.selectedPhotos,
      };
    case ALBUM_FOLDER_PHOTOS_TOGGLE_TARGET_ALBUM: {
      const isTargetSelected = !!state.targetAlbums.find(
        target =>
          action.target.albumId === target.albumId &&
          action.target.photoGroupId === target.photoGroupId,
      );

      const isAlbumSelected = !!state.targetAlbums.find(
        target => target.albumId === action.target.albumId,
      );

      let targetAlbums;

      if (isTargetSelected) {
        targetAlbums = state.targetAlbums.filter(
          target =>
            action.target.albumId !== target.albumId ||
            action.target.photoGroupId !== target.photoGroupId,
        );
      } else if (isAlbumSelected) {
        targetAlbums = state.targetAlbums.map(target => {
          // Allow a single photo group per album
          if (target.albumId === action.target.albumId) {
            return action.target;
          }

          return target;
        });
      } else {
        targetAlbums = [...state.targetAlbums, action.target];
      }

      return {
        ...state,
        targetAlbums,
      };
    }
    case ALBUM_FOLDER_PHOTOS_TOGGLE_ASSIGNED_ALBUM_FOLDER_TAG_ID: {
      const isSelected = !!state.assignedAlbumFolderTagIds.includes(
        action.albumFolderTagId,
      );

      return {
        ...state,
        assignedAlbumFolderTagIds: isSelected
          ? state.assignedAlbumFolderTagIds.filter(
              id => id !== action.albumFolderTagId,
            )
          : [...state.assignedAlbumFolderTagIds, action.albumFolderTagId],
      };
    }

    case ALBUM_FOLDER_PHOTOS_ASSIGN_REQUEST:
      return {
        ...state,
        assigning: true,
        error: null,
      };

    case ALBUM_FOLDER_PHOTOS_ASSIGN_SUCCESS:
      return {
        ...state,
        assigning: false,
        photos: action.selectAll
          ? state.photos.filter(photoId => action.photos.includes(photoId))
          : state.photos.filter(photoId => !action.photos.includes(photoId)),
        targetAlbums: action.keepTargetAlbums ? state.targetAlbums : [],
        selectedPhotos: [],
        multiSelect: false,
      };

    case ALBUM_FOLDER_PHOTOS_ASSIGN_FAILURE:
      return {
        ...state,
        targetAlbums: action.keepTargetAlbums ? state.targetAlbums : [],
        assigning: false,
        error: action.error,
      };

    case ALBUM_FOLDER_PHOTOS_DO_NOT_SHOW_ASSIGN_CONFIRMATION:
      return {
        ...state,
        showAssignPhotoConfirmation: false,
      };

    case ALBUM_FOLDER_PHOTOS_SELECT_ALL_FOR_REVIEW:
      return {
        ...state,
        selectedPhotos: [...state.photos],
        multiSelect: true,
      };

    case ALBUM_FOLDER_PHOTOS_SELECT_ALL:
      return {
        ...state,
        multiSelect: true,
        selectAll: true,
      };

    case ALBUM_FOLDER_PHOTOS_DESELECT_ALL:
      return {
        ...state,
        selectedPhotos: [],
        selectAll: false,
        multiSelect: true,
      };

    case ALBUM_FOLDER_PHOTOS_SELECT_PHOTOS:
      return {
        ...state,
        selectedPhotos: uniqBy([...state.selectedPhotos, ...action.photos]),
        multiSelect: true,
      };

    case ALBUM_FOLDER_PHOTOS_DESELECT_PHOTOS:
      return {
        ...state,
        selectedPhotos: state.selectedPhotos.filter(
          photoId => !action.photos.includes(photoId),
        ),
        multiSelect: true,
      };

    case ALBUM_FOLDER_PHOTOS_CLEAR_TARGET_ALBUMS: {
      return {
        ...state,
        targetAlbums: [],
      };
    }

    case ALBUM_FOLDER_PHOTOS_CLEAR_ASSIGNED_ALBUM_FOLDER_TAG_IDS: {
      return {
        ...state,
        assignedAlbumFolderTagIds: [],
      };
    }

    case UPLOAD_RESET:
      return {
        ...initialState,
        // Mainly a safeguard. We might reset the upload but want
        // to keep the targets and assignments.
        targetAlbums: state.targetAlbums,
        assignedAlbumFolderTagIds: state.assignedAlbumFolderTagIds,
      };

    case ALBUM_FOLDER_PHOTOS_RESET: {
      if (action.albumFolderId === state.albumFolderId) {
        // do not reset
        return state;
      }

      return {
        ...initialState,
        albumFolderId: action.albumFolderId,
      };
    }

    default:
      return state;
  }
};

export default albumFolderUploadsReducer;
